import { useEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import { handleProfileAndCoverUpload } from '../../utils/handleProfileAndCoverUpload';
import { getPhotoBinding, getSavedFraming, isPositionableImage, PHOTO_FIELDS } from './photoFraming';

export default function useProfilePhotoPositioning({ player, viewedPlayer, profileId, setViewedPlayer,
  updatePlayer, coverRef, avatarRef, refresh }) {
  const [session, setSession] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const live = useRef();
  const mounted = useRef(true);
  const uploading = useRef(false);
  live.current = { player, viewedPlayer, profileId };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setSession(null); setUploadError(''); }, [profileId, player?.data?.player?.playerId]);
  const owns = (snapshot) => Boolean(snapshot.player?.data?.player?.playerId &&
    snapshot.viewedPlayer?.playerId === snapshot.player.data.player.playerId &&
    snapshot.profileId === snapshot.viewedPlayer.playerId);
  const sameView = (snapshot) => mounted.current && owns(live.current) &&
    snapshot.profileId === live.current.profileId &&
    snapshot.player?.data?.player?.playerId === live.current.player?.data?.player?.playerId;
  const geometry = () => {
    const cover = coverRef.current?.getBoundingClientRect();
    const avatar = avatarRef.current?.getBoundingClientRect();
    return {
      aspect: cover?.width && cover?.height ? cover.width / cover.height : 3,
      overlap: cover?.width && avatar?.width ? {
        left: `${100 * (avatar.left - cover.left) / cover.width}%`,
        top: `${100 * (avatar.top - cover.top) / cover.height}%`,
        width: `${100 * avatar.width / cover.width}%`,
        height: `${100 * avatar.height / cover.height}%`,
      } : null,
    };
  };
  const open = (target, sourcePlayer = viewedPlayer, replaced = false) => {
    if (!owns(live.current)) return;
    const binding = getPhotoBinding(sourcePlayer, target);
    if (!isPositionableImage(binding.sourceUrl)) return;
    setSession({ target, ...binding, framing: getSavedFraming(sourcePlayer, target),
      avatarUrl: sourcePlayer.profilePhoto, replaced, ...geometry() });
  };
  useEffect(() => {
    if (!session || session.target !== 'cover') return undefined;
    const resize = () => setSession((previous) => previous ? { ...previous, ...geometry() } : null);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
    if (coverRef.current) observer?.observe(coverRef.current);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', resize); };
    // Only subscribe for the lifetime of an open cover editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.target]);
  const close = () => { setSession(null); refresh(); };
  const save = async (opened, framing) => {
    const snapshot = live.current;
    const isCurrent = () => {
      const binding = getPhotoBinding(live.current.viewedPlayer, opened.target);
      return sameView(snapshot) && binding.sourceUrl === opened.sourceUrl && binding.sourceVersion === opened.sourceVersion;
    };
    if (!isCurrent()) throw Object.assign(new Error('Photo changed'), { response: { status: 409 } });
    const { data } = await axiosConn.patch('players/me/photo-adjustment', {
      target: opened.target, sourceUrl: opened.sourceUrl, sourceVersion: opened.sourceVersion,
      focalX: framing.focalX, focalY: framing.focalY, zoom: framing.zoom,
    }, { headers: { Authorization: `Bearer ${snapshot.player.token}` } });
    if (!isCurrent()) return;
    const field = `${PHOTO_FIELDS[opened.target]}Adjustment`;
    const adjustment = data?.data?.player?.[field];
    if (!adjustment || adjustment.sourceUrl !== opened.sourceUrl || adjustment.sourceVersion !== opened.sourceVersion) {
      throw new Error('Invalid adjustment response');
    }
    setViewedPlayer((previous) => ({ ...previous, [field]: adjustment }));
    updatePlayer({ ...live.current.player.data.player, [field]: adjustment });
    setSession(null);
  };
  const upload = async (target, event, options) => {
    if (uploading.current || !owns(live.current)) return;
    const input = event.target;
    const file = input.files?.item(input.files.length - 1);
    if (!file) return;
    const snapshot = live.current;
    uploading.current = true; setUploadError('');
    try {
      await handleProfileAndCoverUpload({ ...options,
        shouldApplyUpdate: () => sameView(snapshot),
        onUploaded: (savedPlayer) => {
          if (sameView(snapshot) && file.type.startsWith('image/')) open(target, savedPlayer, true);
        },
        onUploadError: () => { if (sameView(snapshot)) setUploadError('Photo was not saved. Please try again.'); },
      })(event);
    } finally { uploading.current = false; input.value = ''; }
  };
  return { session, open, close, save, upload, uploadError };
}
